import { untrack } from 'svelte';
import { audio } from '../../audio/AudioManager';
import type { AudioSettings } from '../../audio/types';
import { setReducedMotionPreference } from '../../game/motion';
import { haptics } from '../../platform/haptics';
import { storage } from '../../platform/storage';
import { i18n } from '../i18n/index.svelte';
import {
  DEFAULT_DISPLAY_SETTINGS,
  DISPLAY_SETTINGS_KEY,
  normaliseDisplaySettings,
  type DisplaySettings,
  type Language,
} from './displaySettings';
import {
  cloneSettingsSnapshot,
  settingsSnapshotsEqual,
  type SettingsSnapshot,
} from './settingsDraft';

/**
 * One reactive facade over every player preference.
 *
 * The values themselves already had homes before this screen existed —
 * `AudioManager` owns the buses and persists them, `haptics` owns vibration —
 * and this deliberately does not duplicate either. It reads and writes through
 * them, and owns only the three preferences that had nowhere else to live:
 * CRT, reduced motion and language.
 *
 * The reason for a facade at all is that a settings screen binding to three
 * different persistence mechanisms, two of them non-reactive, is a screen that
 * silently drifts out of sync with what is actually in effect.
 *
 * It also owns editing sessions. Outside one, every setter writes through as it
 * always did — the rest of the app changes volume and language without knowing
 * this concept exists. Inside one, changes apply live but stay off disk, so the
 * screen can be tried and then abandoned. `baseline` is what was saved when the
 * session opened, and it is both what `dirty` compares against and what
 * `discard` restores.
 */

export type { DisplaySettings, Language } from './displaySettings';

class Settings {
  display = $state<DisplaySettings>({ ...DEFAULT_DISPLAY_SETTINGS });
  audio = $state<AudioSettings>(audio.getSettings());
  hapticsEnabled = $state(true);

  /** Set once the persisted values have been read, so the UI can wait. */
  ready = $state(false);

  /** What was on disk when the editing session opened; null outside one. */
  private baseline = $state<SettingsSnapshot | null>(null);

  /** Previewed but unsaved. Drives the save button and the exit prompt. */
  dirty = $derived(
    this.baseline !== null && !settingsSnapshotsEqual(this.currentSnapshot(), this.baseline),
  );

  private unsubscribeAudio?: () => void;

  /**
   * Called once at boot, before the first view mounts. Loading late would show
   * the defaults for a frame and then snap — and, worse, would let a CRT the
   * player had turned off flash on every cold start.
   */
  async init(): Promise<void> {
    if (this.ready) return;

    this.display = normaliseDisplaySettings(await storage.getJson(DISPLAY_SETTINGS_KEY, null));
    applyDisplay(this.display);

    // `subscribe` fires immediately with the current values and again on every
    // change, including ones made from outside this screen.
    this.unsubscribeAudio ??= audio.subscribe((next) => (this.audio = next));
    this.hapticsEnabled = haptics.isEnabled();
    this.ready = true;
  }

  // --- display ---------------------------------------------------------------

  setCrt(enabled: boolean): void {
    this.patchDisplay({ crt: enabled });
  }

  setReducedMotion(enabled: boolean): void {
    this.patchDisplay({ reducedMotion: enabled });
  }

  setLanguage(language: Language): void {
    this.patchDisplay({ language });
  }

  // --- audio and haptics -----------------------------------------------------

  /** The master mute is what the mockup's single "SONIDO" switch controls. */
  setSoundEnabled(enabled: boolean): void {
    audio.setMuted('master', !enabled, { persist: this.baseline === null });
  }

  setVolume(bus: 'music' | 'sfx', volume: number): void {
    audio.setVolume(bus, volume, { persist: this.baseline === null });
  }

  setHaptics(enabled: boolean): void {
    haptics.setEnabled(enabled, { persist: this.baseline === null });
    this.hapticsEnabled = enabled;
  }

  // --- editing session -------------------------------------------------------

  /**
   * Open a session. Idempotent: the settings screen calls it on mount, and
   * re-opening it would take a baseline that already includes the changes the
   * player has not saved — which is exactly how unsaved work goes missing.
   *
   * Untracked because the caller is an effect. Reading the live values to
   * snapshot them would subscribe that effect to every preference on the
   * screen, so the first change would tear the session down and open a new one
   * around the value that had just changed: the baseline would chase the edits
   * and nothing would ever look unsaved.
   */
  beginEdit(): void {
    untrack(() => {
      this.baseline ??= cloneSettingsSnapshot(this.currentSnapshot());
    });
  }

  /**
   * Close the session, leaving whatever is in effect in effect. For a screen
   * being left with nothing pending; `save` and `discard` are the two ways to
   * close one that has something pending.
   */
  endEdit(): void {
    this.baseline = null;
  }

  /** Commit the previewed values through the three systems that own them. */
  save(): void {
    void storage.setJson(DISPLAY_SETTINGS_KEY, this.display);
    audio.persistSettings();
    haptics.persistSettings();
    this.baseline = cloneSettingsSnapshot(this.currentSnapshot());
  }

  /**
   * Put back what was saved. Nothing was written while the session was open, so
   * this only has to re-apply the baseline live — the disk already agrees with
   * it, and rewriting it would be a save the player just declined.
   */
  discard(): void {
    const baseline = this.baseline;
    if (!baseline) return;

    for (const bus of ['master', 'music', 'sfx'] as const) {
      audio.setVolume(bus, baseline.audio[bus].volume, { persist: false });
      audio.setMuted(bus, baseline.audio[bus].muted, { persist: false });
    }

    haptics.setEnabled(baseline.hapticsEnabled, { persist: false });
    this.hapticsEnabled = baseline.hapticsEnabled;
    this.patchDisplay(baseline.display);

    this.baseline = cloneSettingsSnapshot(baseline);
  }

  private currentSnapshot(): SettingsSnapshot {
    return { display: this.display, audio: this.audio, hapticsEnabled: this.hapticsEnabled };
  }

  private patchDisplay(patch: Partial<DisplaySettings>): void {
    this.display = { ...this.display, ...patch };
    applyDisplay(this.display);
    // Durante una sesión de edición el disco conserva lo último guardado: es lo
    // que hace que descartar sea gratis y que cerrar la app a medias no deje
    // preferencias que nadie confirmó.
    if (this.baseline === null) void storage.setJson(DISPLAY_SETTINGS_KEY, this.display);
  }
}

/**
 * Push the display preferences into the two systems that act on them. Kept out
 * of the class so `init` and every later change take exactly the same path —
 * the classic bug here is a setting that works when toggled but not when
 * restored from disk.
 */
function applyDisplay(display: DisplaySettings): void {
  setReducedMotionPreference(display.reducedMotion);
  document.documentElement.dataset.crt = display.crt ? 'on' : 'off';
  document.documentElement.lang = display.language;
  // The catalogue is pushed the same way as the other two, and for the same
  // reason: one owner, one path. `i18n` never reads the preference back, so the
  // language on screen cannot disagree with the one on disk.
  i18n.setLanguage(display.language);
}

export const settings = new Settings();
